import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import type { BufferedImageLayers, UseBufferedImageParams } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/buffered-image/types';
import { createBufferedImageLayer, decodeImage, getBufferedImageLayerKey, getBufferedImageSourceKey, setBufferedImageLayerSettled } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/buffered-image/utils';
import { PREVIEW_CROSSFADE_DURATION_MS } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/constants';
import type { SpartanCustomizePreviewTone } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/preview-tone';
export const useBufferedImage = t0 => {
  const $ = _c(64);
  const {
    src,
    onImageLoad,
    onImageError,
    onSwapStart,
    swapEffect,
    holdSurfaceTone,
    surfaceClassName,
    surfaceTone
  } = t0;
  let t1;
  if ($[0] !== src || $[1] !== surfaceClassName || $[2] !== surfaceTone) {
    t1 = () => [createBufferedImageLayer(src, surfaceClassName, surfaceTone), null];
    $[0] = src;
    $[1] = surfaceClassName;
    $[2] = surfaceTone;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [layers, setLayers] = useState(t1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [incomingIndex, setIncomingIndex] = useState(null);
  const [isIncomingVisible, setIsIncomingVisible] = useState(false);
  const activeLayer = layers[activeIndex];
  let t2;
  if ($[4] !== activeLayer || $[5] !== holdSurfaceTone || $[6] !== src || $[7] !== surfaceTone) {
    t2 = holdSurfaceTone && activeLayer && getBufferedImageSourceKey(activeLayer.src) === getBufferedImageSourceKey(src) ? activeLayer.surfaceTone : surfaceTone;
    $[4] = activeLayer;
    $[5] = holdSurfaceTone;
    $[6] = src;
    $[7] = surfaceTone;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const effectiveSurfaceTone = t2;
  let t3;
  if ($[9] !== effectiveSurfaceTone || $[10] !== src) {
    t3 = getBufferedImageLayerKey(src, effectiveSurfaceTone);
    $[9] = effectiveSurfaceTone;
    $[10] = src;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const layerKey = t3;
  const onImageLoadRef = useRef(onImageLoad);
  const onImageErrorRef = useRef(onImageError);
  const onSwapStartRef = useRef(onSwapStart);
  const isMountedRef = useRef(true);
  const layerKeyRef = useRef(layerKey);
  const loadingIndexRef = useRef(null);
  const transitionFrameRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)") ?? false;
  let t4;
  let t5;
  if ($[12] !== onImageLoad) {
    t4 = () => {
      onImageLoadRef.current = onImageLoad;
    };
    t5 = [onImageLoad];
    $[12] = onImageLoad;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] !== onImageError) {
    t6 = () => {
      onImageErrorRef.current = onImageError;
    };
    t7 = [onImageError];
    $[15] = onImageError;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[18] !== onSwapStart) {
    t8 = () => {
      onSwapStartRef.current = onSwapStart;
    };
    t9 = [onSwapStart];
    $[18] = onSwapStart;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== layerKey) {
    t10 = () => {
      layerKeyRef.current = layerKey;
    };
    t11 = [layerKey];
    $[21] = layerKey;
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[24] !== loadingIndex) {
    t12 = () => {
      loadingIndexRef.current = loadingIndex;
    };
    t13 = [loadingIndex];
    $[24] = loadingIndex;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      if (transitionFrameRef.current !== null) {
        cancelAnimationFrame(transitionFrameRef.current);
        transitionFrameRef.current = null;
      }
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const clearTransition = t14;
  let t15;
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      isMountedRef.current = true;
      return () => {
        clearTransition();
        isMountedRef.current = false;
      };
    };
    t16 = [clearTransition];
    $[28] = t15;
    $[29] = t16;
  } else {
    t15 = $[28];
    t16 = $[29];
  }
  useEffect(t15, t16);
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = index => {
      loadingIndexRef.current = index;
      setLoadingIndex(index);
    };
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const setLoadingLayer = t17;
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = index_0 => {
      setActiveIndex(index_0);
      setIncomingIndex(null);
      setIsIncomingVisible(false);
      setLoadingLayer(null);
    };
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const commitLayer = t18;
  let t19;
  if ($[32] !== prefersReducedMotion || $[33] !== swapEffect) {
    t19 = (index_1, nextSurfaceTone, t20) => {
      const shouldCrossfade = t20 === undefined ? true : t20;
      clearTransition();
      onSwapStartRef.current?.(nextSurfaceTone, shouldCrossfade);
      if (swapEffect !== "surfaceCrossfade" || !shouldCrossfade || prefersReducedMotion) {
        commitLayer(index_1);
        return;
      }
      setIncomingIndex(index_1);
      setIsIncomingVisible(false);
      setLoadingLayer(null);
    };
    $[32] = prefersReducedMotion;
    $[33] = swapEffect;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const showLayer = t19;
  let t20;
  let t21;
  if ($[35] !== incomingIndex) {
    t20 = () => {
      if (incomingIndex === null) {
        return;
      }
      transitionFrameRef.current = requestAnimationFrame(() => {
        transitionFrameRef.current = requestAnimationFrame(() => {
          transitionFrameRef.current = null;
          if (!isMountedRef.current) {
            return;
          }
          setIsIncomingVisible(true);
          transitionTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) {
              return;
            }
            commitLayer(incomingIndex);
            transitionTimeoutRef.current = null;
          }, PREVIEW_CROSSFADE_DURATION_MS);
        });
      });
      return clearTransition;
    };
    t21 = [clearTransition, commitLayer, incomingIndex];
    $[35] = incomingIndex;
    $[36] = t20;
    $[37] = t21;
  } else {
    t20 = $[36];
    t21 = $[37];
  }
  useEffect(t20, t21);
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = (index_2, key, isReady) => {
      setLayers(prev => setBufferedImageLayerSettled(prev, index_2, key, isReady));
    };
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const markLayerSettled = t22;
  let t23;
  let t24;
  if ($[39] !== activeIndex || $[40] !== effectiveSurfaceTone || $[41] !== incomingIndex || $[42] !== layerKey || $[43] !== layers || $[44] !== loadingIndex || $[45] !== showLayer || $[46] !== src || $[47] !== surfaceClassName) {
    t23 = () => {
      const activeLayer_0 = layers[activeIndex];
      if (incomingIndex !== null) {
        return;
      }
      if (activeLayer_0?.key === layerKey) {
        if (loadingIndex !== null) {
          setLoadingLayer(null);
        }
        return;
      }
      const existingIndex = layers.findIndex(layer => layer?.key === layerKey);
      if (existingIndex >= 0) {
        if (layers[existingIndex]?.isFailed) {
          setLayers(prev_0 => {
            const nextLayers = [...prev_0];
            nextLayers[existingIndex] = createBufferedImageLayer(src, surfaceClassName, effectiveSurfaceTone);
            return nextLayers;
          });
          setLoadingLayer(existingIndex);
          return;
        }
        if (!layers[existingIndex]?.isReady) {
          setLoadingLayer(existingIndex);
          return;
        }
        onImageLoadRef.current?.();
        showLayer(existingIndex, layers[existingIndex]?.surfaceTone);
        return;
      }
      const nextLoadingIndex = activeIndex === 0 ? 1 : 0;
      setLayers(prev_1 => {
        const nextLayers_0 = [...prev_1];
        nextLayers_0[nextLoadingIndex] = createBufferedImageLayer(src, surfaceClassName, effectiveSurfaceTone);
        return nextLayers_0;
      });
      setLoadingLayer(nextLoadingIndex);
    };
    t24 = [activeIndex, incomingIndex, layers, loadingIndex, setLoadingLayer, showLayer, src, layerKey, effectiveSurfaceTone, surfaceClassName];
    $[39] = activeIndex;
    $[40] = effectiveSurfaceTone;
    $[41] = incomingIndex;
    $[42] = layerKey;
    $[43] = layers;
    $[44] = loadingIndex;
    $[45] = showLayer;
    $[46] = src;
    $[47] = surfaceClassName;
    $[48] = t23;
    $[49] = t24;
  } else {
    t23 = $[48];
    t24 = $[49];
  }
  useEffect(t23, t24);
  let t25;
  if ($[50] !== layers || $[51] !== showLayer) {
    t25 = (index_3, image) => {
      const layer_0 = layers[index_3];
      if (!layer_0) {
        return;
      }
      decodeImage(image).then(() => {
        if (!isMountedRef.current) {
          return;
        }
        markLayerSettled(index_3, layer_0.key, true);
        if (layer_0.key === layerKeyRef.current) {
          onImageLoadRef.current?.();
        }
        if (layer_0.key !== layerKeyRef.current || index_3 !== loadingIndexRef.current) {
          return;
        }
        showLayer(index_3, layer_0.surfaceTone);
      });
    };
    $[50] = layers;
    $[51] = showLayer;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const handleLayerLoad = t25;
  let t26;
  if ($[53] !== activeIndex || $[54] !== layers || $[55] !== showLayer) {
    t26 = index_4 => {
      const layer_1 = layers[index_4];
      if (!layer_1) {
        return;
      }
      markLayerSettled(index_4, layer_1.key, false);
      if (layer_1.key !== layerKeyRef.current) {
        return;
      }
      onImageErrorRef.current?.();
      if (index_4 === activeIndex) {
        commitLayer(index_4);
        return;
      }
      showLayer(index_4, layer_1.surfaceTone);
    };
    $[53] = activeIndex;
    $[54] = layers;
    $[55] = showLayer;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  const handleLayerError = t26;
  let t27;
  if ($[57] !== activeIndex || $[58] !== handleLayerError || $[59] !== handleLayerLoad || $[60] !== incomingIndex || $[61] !== isIncomingVisible || $[62] !== layers) {
    t27 = {
      layers,
      activeIndex,
      incomingIndex,
      isIncomingVisible,
      handleLayerLoad,
      handleLayerError
    };
    $[57] = activeIndex;
    $[58] = handleLayerError;
    $[59] = handleLayerLoad;
    $[60] = incomingIndex;
    $[61] = isIncomingVisible;
    $[62] = layers;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  return t27;
};